import { ToastrService } from "ngx-toastr";
import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { GlobalConstants } from "app/common/global-constants";
import {
  UserActionEnum,
  UserPermissionsStatus,
  resourceType,
} from "../../../../common/manage-permission";
import { AuthenticationService } from "../../../../services/authentication.service";
import { Subject } from "rxjs";
import { first, take, takeUntil } from "rxjs/operators";
import { viewLabels } from "../../../../common/common-constants";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { ConfigurationService } from "../../../../services/configuration.service";
import {
  filterByCarrierName,
  transformRequestedResourcePermissions,
} from "../../../../common/permission-utils";

@Component({
  selector: "app-manage-user-permissions",
  templateUrl: "./manage-user-permissions.component.html",
  styleUrls: ["./manage-user-permissions.component.scss"],
})
export class ManageUserPermissionsComponent implements OnInit, OnDestroy {
  public loading = false;
  public getAllRequestedPermissionsUrl =
    GlobalConstants.GET_ALL_REQUESTED_PERMISSIONS;
  public updateRequestedPermissionsUrl =
    GlobalConstants.UPDATE_REQUESTED_PERMISSIONS;
  public createBindleResourceURL =
    GlobalConstants.CREATE_BINDLE_RESOURCE_ENDPOINT;
  public checkResourceReferenceUrl = GlobalConstants.CHECK_RESOURCE_REFERENCE;
  public requestedResourcePermissions = [];
  public requestedResourcePermissionsList = [];
  public user: any;
  public carrierName: string = "";
  private destroy$ = new Subject<void>();
  public viewLabels = viewLabels;
  public parentResourceIdList: { label: string; value: string }[] = [];

  constructor(
    @Inject("RequestsService") private requestsService,
    @Inject("AuthenticationService")
    private readonly authenticationService: AuthenticationService,
    private toastrService: ToastrService,
    @Inject("DialogService") private dialogService,
    @Inject("ConfigurationService")
    private readonly configurationService: ConfigurationService,
  ) {
    configurationService
      .getSettings()
      .pipe(first())
      .subscribe((settings) => {
        this.parentResourceIdList = settings.parentResourceIdList;
      });
  }

  ngOnInit(): void {
    this.getAllRequestedPermissions();
    this.user = this.authenticationService.getUserDetails();
  }

  public findByCarrierName() {
    this.requestedResourcePermissions = filterByCarrierName(
      this.requestedResourcePermissionsList,
      this.carrierName,
    );
  }

  private getAllRequestedPermissions() {
    this.loading = true;
    this.requestsService
      .put(this.getAllRequestedPermissionsUrl, {
        requestStatusList: [UserPermissionsStatus.PENDING],
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.loading = false;
          this.requestedResourcePermissionsList =
            transformRequestedResourcePermissions(data?.resourcePermissions);
          this.requestedResourcePermissions =
            this.requestedResourcePermissionsList;
        },
        (error) => {
          this.loading = false;
          this.toastrService.error(error, GlobalConstants.TITLE_ERROR);
        },
      );
  }

  public approveUserPermission(requestedPermission) {
    this.createRequestedUserPermissions(requestedPermission);
  }

  public rejectUserPermission(requestedPermission) {
    this.dialogService
      .openRejectResourcePermissionsDialog()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data.action === UserActionEnum.SUBMIT) {
          this.updateRequestedUserPermissions({
            actorId: requestedPermission?.actorId,
            requestTimestamp: requestedPermission?.requestTimestamp,
            requestStatus: UserPermissionsStatus.REJECTED,
            reviewedReason: data?.permissionRejectReason,
            reviewedBy: this.user?.alias,
          });
        }
      });
  }

  private createRequestedUserPermissions(requestedPermission) {
    if (!requestedPermission?.requestedPermissions?.length) {
      this.toastrService.error('No permissions to create', GlobalConstants.TITLE_ERROR);
      return;
    }
    const resourceToBeCreatedList =
      requestedPermission?.requestedPermissions?.map((permission) => {
        const requestObject = {
          name: permission?.resourceName,
          resourceType: {
            resourceTypeName: permission?.resourceType,
            namespaceName: permission?.resourceNamespace,
          },
          parent: {
            resourceId: this.parentResourceIdList[0]?.value,
          },
        };
        return this.requestsService
          .put(this.createBindleResourceURL, requestObject)
          .pipe(
            take(1),
            catchError((err) => of({ error: err, requestObject })), // return fallback instead of failing all
          );
      });

    this.loading = true;
    forkJoin(resourceToBeCreatedList)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (results) => {
          this.loading = false;
          this.checkResourceReference(requestedPermission);
        },
        (error) => {
          this.loading = false;
          this.toastrService.error(error, GlobalConstants.TITLE_ERROR);
        },
      );
  }

  private checkResourceReference(requestedPermission) {
    const resourceReferenceList =
      requestedPermission?.requestedPermissions?.map((permission) => {
        const requestObject = {
          resourceName: permission?.resourceName,
          resourceType: {
            resourceTypeName: permission?.resourceType,
            namespaceName: permission?.resourceNamespace,
          },
        };
        return requestObject;
      });

    const BATCH_SIZE = 100;
    const checkResourceBatches = [];
    for (let i = 0; i < resourceReferenceList.length; i += BATCH_SIZE) {
      checkResourceBatches.push(resourceReferenceList.slice(i, i + BATCH_SIZE));
    }

    const checkResourceBatchRequests = checkResourceBatches.map(
      (resourceReferenceList) => {
        return this.requestsService
          .put(this.checkResourceReferenceUrl, { resourceReferenceList })
          .pipe(
            take(1),
            catchError((err) => of({ error: err, resourceReferenceList })),
          );
      },
    );

    this.loading = true;
    forkJoin(checkResourceBatchRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (results) => {
          this.loading = false;
          let availableResourceReferences = {};
          results.forEach((result: any) => {
            availableResourceReferences = {
              ...availableResourceReferences,
              ...result?.resourceDescriptionResults,
            };
          });

          if (
            Object.keys(availableResourceReferences)?.length ===
            resourceReferenceList?.length
          ) {
            this.updateRequestedUserPermissions({
              actorId: requestedPermission?.actorId,
              requestTimestamp: requestedPermission?.requestTimestamp,
              requestStatus: UserPermissionsStatus.APPROVED,
              reviewedBy: this.user?.alias,
            });
          } else {
            this.toastrService.error(
              "Some of the resources are not created successfully please try again after sometime",
              GlobalConstants.TITLE_ERROR,
            );
          }
        },
        (error) => {
          this.loading = false;
          this.toastrService.error(error, GlobalConstants.TITLE_ERROR);
        },
      );
  }

  private updateRequestedUserPermissions(requestObject) {
    this.loading = true;
    this.requestsService
      .put(this.updateRequestedPermissionsUrl, requestObject)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.loading = false;
          this.toastrService.success(
            GlobalConstants.REQUEST_SUCCESSFUL,
            GlobalConstants.TITLE_SUCCESS,
          );
          this.getAllRequestedPermissions();
        },
        (error) => {
          this.loading = false;
          this.toastrService.error(error, GlobalConstants.TITLE_ERROR);
        },
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
